/**
 * Utility functions for date formatting
 */

/**
 * Convert ISO date string to yyyy-MM-dd format for HTML5 date inputs
 * @param dateString - ISO date string (e.g., "1985-05-15T00:00:00.000000Z")
 * @returns Date string in yyyy-MM-dd format or empty string if invalid
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Convert yyyy-MM-dd format to ISO date string
 * @param dateString - Date string in yyyy-MM-dd format
 * @returns ISO date string
 */
export const formatDateForApi = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString();
  } catch (error) {
    console.error('Error converting date to ISO:', error);
    return '';
  }
};

/**
 * Format date/time string to IST format with AM/PM
 * @param dateString - Date string in any format
 * @param convertToIst - Whether to convert UTC to IST (default: true)
 * @returns Formatted date/time string in DD/MM/YYYY h:mm AM/PM format
 */
export function formatDateTimeIst(dateString: string | null | undefined, convertToIst: boolean = true): string {
  if (!dateString) return '';
  
  try {
    // Handle inputs with or without timezone suffix
    const date = dateString.includes('Z') || dateString.includes('+') || dateString.includes('-') 
      ? new Date(dateString)
      : new Date(dateString + 'Z');
    if (isNaN(date.getTime())) return '';
    
    if (convertToIst) {
      // Format using IST timezone conversion (for UTC timestamps like created_at)
      // If input has timezone info (Z, +, -), use toLocaleString for proper conversion
      // If input has no timezone info, add 5.5 hours manually
      if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-')) {
        // Input has timezone info - use toLocaleString for proper conversion
        const options: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZone: 'Asia/Kolkata'
        };
        
        return date.toLocaleString('en-IN', options)
          .replace(/\//g, '/')
          .replace(',', '');
      } else {
        // Input has no timezone info - add 5.5 hours manually
        const istTimestamp = date.getTime() + (5.5 * 60 * 60 * 1000);
        const istDate = new Date(istTimestamp);
        
        const day = String(istDate.getDate()).padStart(2, '0');
        const month = String(istDate.getMonth() + 1).padStart(2, '0');
        const year = istDate.getFullYear();
        const hours24 = istDate.getHours();
        const hours = String(hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24).padStart(2, '0');
        const minutes = String(istDate.getMinutes()).padStart(2, '0');
        const meridiem = hours24 >= 12 ? 'PM' : 'AM';
        
        return `${day}/${month}/${year} ${hours}:${minutes} ${meridiem}`;
      }
    } else {
      // Format as-is without timezone conversion (for requested_slot_datetime)
      // Use UTC methods to get the original UTC time
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours24 = date.getUTCHours();
      const hours = String(hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const meridiem = hours24 >= 12 ? 'PM' : 'AM';
      
      return `${day}/${month}/${year} ${hours}:${minutes} ${meridiem}`;
    }
  } catch (error) {
    console.error('Error formatting datetime IST display:', error);
    return '';
  }
};

